import React, {
  DragEventHandler,
  ReactEventHandler,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import createLinkPlugin from '@draft-js-plugins/anchor'
import '@draft-js-plugins/anchor/lib/plugin.css'
import {
  BlockquoteButton,
  BoldButton,
  ItalicButton,
  OrderedListButton,
  UnderlineButton,
  UnorderedListButton,
} from '@draft-js-plugins/buttons'
import Editor from '@draft-js-plugins/editor'
import createEmojiPlugin, { defaultTheme } from '@draft-js-plugins/emoji'
import '@draft-js-plugins/emoji/lib/plugin.css'
import createMentionPlugin from '@draft-js-plugins/mention'
import '@draft-js-plugins/mention/lib/plugin.css'
import createToolbarPlugin, { Separator } from '@draft-js-plugins/static-toolbar'
import { PaperAirplaneIcon } from '@heroicons/react/20/solid'
import { DocumentPlusIcon } from '@heroicons/react/24/outline'
import { EditorState, convertFromRaw, convertToRaw } from 'draft-js'
import draftToHtml from 'draftjs-to-html'
import _debounce from 'lodash/debounce'
import { toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

import { useStreamingNotes } from './useStreamingNotes'
import { customEntityTransform, suggestionsFilter } from './utils'
import { FileListPreview } from '@/components/FileListPreview'
import Spinner from '@/components/Spinner'
import {
  useDocumentUploadFinishMutation,
  useMultiDocumentUploadStartMutation,
} from '@/services/documents'
import { useUploadFileMutation } from '@/services/s3'
import { useLazyListApplicationProfilesQuery } from '@/services/v2-application/applicationProfile'
import { StreamingNote, UserData } from '@/types/unlock'

interface IMessageComposer {
  applicationId: number
  applicationExternalId?: string
  createStreamingNote: (note: Partial<StreamingNote>) => Promise<unknown>
  isCreatingStreamingNote?: boolean
  parentStreamingNoteId?: number
  primaryApplicantProfileId?: number
  placeholder?: string
}

export function MessageComposer({
  applicationId,
  applicationExternalId,
  createStreamingNote,
  isCreatingStreamingNote,
  parentStreamingNoteId,
  placeholder = 'Jot something down, type ":" for emojis',
}: IMessageComposer) {
  const userDraftSSKey = 'unlock:streamingNotes:userDraft' // SSKey = Session Storage Key
  const editor = useRef<Editor>(null)
  const draft = useMemo(() => {
    if (typeof window !== 'undefined') {
      return window.sessionStorage.getItem(userDraftSSKey)
    }
  }, [typeof window])
  const [editorState, setEditorState] = useState(() => {
    if (draft) {
      return EditorState.createWithContent(convertFromRaw(JSON.parse(draft)))
    }
    return EditorState.createEmpty()
  })
  const [open, setOpen] = useState(false)
  const { mentions } = useStreamingNotes()
  const [suggestions, setSuggestions] = useState(mentions)
  const inputFileEl = useRef<HTMLInputElement>(null)
  const [files, setFiles] = useState<File[]>([])
  const filesRef = useRef(files)
  const editorWrapperEl = useRef<HTMLDivElement>(null)

  // FIXME: These should prob be a level higher
  const [muiltiDocumentUploadStart] = useMultiDocumentUploadStartMutation()
  const [documentUploadFinish] = useDocumentUploadFinishMutation()
  const [uploadFile] = useUploadFileMutation()
  const [getApplicationProfiles] = useLazyListApplicationProfilesQuery()
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // #region DraftJS Editor bootstrap plugins
  const { plugins, MentionSuggestions, Toolbar, EmojiSuggestions, linkPlugin } = useMemo(() => {
    const mentionPlugin = createMentionPlugin({ entityMutability: 'IMMUTABLE' })
    const { MentionSuggestions } = mentionPlugin

    const toolbarPlugin = createToolbarPlugin()
    const { Toolbar } = toolbarPlugin

    const linkPlugin = createLinkPlugin({
      placeholder: 'Paste a link and press enter',
      linkTarget: '_blank',
    })

    const emojiPlugin = createEmojiPlugin({
      useNativeArt: true,
      priorityList: {
        ':white_check_mark:': ['2705'],
        ':ok_hand:': ['1f44c'],
        ':thumbsup:': ['1f44d'],
        ':smile:': ['1f604'],
        ':unlock:': ['1F513'],
        ':tada:': ['1f389'],
        ':raised_hands:': ['1f64c'],
        ':100:': ['1f4af'],
        ':heart:': ['2764-fe0f', '2764'],
      },
      theme: {
        ...defaultTheme,
        emojiSelectPopover: 'absolute bg-white border shadow z-10 bottom-20 right-20',
        emojiSelectPopoverEntry: 'text-xl',
        emojiSelectPopoverEntryFocused: 'text-xl',
        emojiSuggestions: 'grid border',
        emojiSuggestionsEntry: 'inline-flex items-center px-5 py-1 line',
        emojiSuggestionsEntryFocused: 'bg-blue-50 items-center inline-flex px-5 py-1',
      },
    })
    const { EmojiSuggestions, EmojiSelect } = emojiPlugin

    const plugins = [emojiPlugin, mentionPlugin, toolbarPlugin, linkPlugin]
    return {
      plugins,
      MentionSuggestions,
      Toolbar,
      EmojiSuggestions,
      EmojiSelect,
      linkPlugin,
    }
  }, [])
  // #endregion DraftJS Editor bootstrap plugins

  async function onSubmit() {
    setIsLoading(true)
    const rawContentState = convertToRaw(editorState.getCurrentContent())

    const hasMentions = Object.values(rawContentState.entityMap).some(
      (entity) => entity.type === 'mention'
    )

    const uniqueTaggedUsers = new Set<UserData>()

    if (hasMentions) {
      for (const entity of Object.values(rawContentState.entityMap)) {
        if (entity.type === 'mention') {
          uniqueTaggedUsers.add(entity.data.mention.id)
        }
      }
    }

    let payload = {
      application: applicationId,
      application_external_id: applicationExternalId,
      text: draftToHtml(rawContentState, null, null, customEntityTransform),
      tagged_users: Array.from(uniqueTaggedUsers),
      parent_streaming_note: null,
      document: null,
      documents: [],
    }

    // When replying, add the parent streaming note id
    if (parentStreamingNoteId) {
      payload.parent_streaming_note = parentStreamingNoteId
    } else {
      delete payload.parent_streaming_note
    }

    if (files.length) {
      try {
        const applicationProfiles = await getApplicationProfiles(applicationExternalId)
        const primaryApplicantProfileId = applicationProfiles?.data?.results?.find(
          (profile) => profile.position === 1
        )?.id

        const payloadMulti = files.map((file) => {
          return {
            file_name: file.name,
            application_profile: primaryApplicantProfileId,
          }
        })

        const resp = await muiltiDocumentUploadStart(payloadMulti).unwrap()

        const promises = files.map(async (doc, i) => {
          const url = resp[i].url
          const uuid = resp[i].uuid
          const id = resp[i].id

          await uploadFile({ url, file: doc })

          await documentUploadFinish(uuid).unwrap()
          return id
        })

        const documents = await Promise.all(promises)

        payload = { ...payload, documents: documents }
      } catch (error) {
        console.error(error)
        setIsLoading(false)
      }
    } else {
      delete payload.document
      delete payload.documents
    }

    createStreamingNote(payload)
      .then(() => {
        // Clear the editor
        setEditorState(EditorState.createEmpty())
        setFiles([])
        setIsLoading(false)
      })
      .catch((error) => {
        console.error(error)
        setIsLoading(false)
      })
  }

  const onChange = (editorState: EditorState) => {
    window.sessionStorage.setItem(
      userDraftSSKey,
      JSON.stringify(convertToRaw(editorState.getCurrentContent()))
    )
    setEditorState(editorState)
  }

  const onOpenChange = (open: boolean) => {
    setOpen(open)
  }

  const onSearchChange = ({ value }: Record<string, string>) => {
    setSuggestions(suggestionsFilter(value, mentions))
  }

  const getPastedFileName = (renamedFiles: File[]) => {
    const filesLookup = [...renamedFiles, ...filesRef.current]
    const pastedFiles = filesLookup?.filter((file) => file.name.includes('image'))

    return `image${pastedFiles.length + 1}.png`
  }

  const maximumFileLengthReached = () => {
    toast.error(() => 'Please attach less than 10 files per note', {
      position: 'bottom-right',
    })
  }

  const cleanUpFileName = (fileName: string) => {
    return fileName.replace(/\s/g, '_')
  }

  const renameFiles = (filesToRename: File[]) => {
    const renamedFiles = []

    for (const fileToRename of filesToRename) {
      if (fileToRename.name === 'image.png') {
        renamedFiles.push(
          new File([fileToRename], getPastedFileName(renamedFiles), {
            type: fileToRename.type,
          })
        )
      } else {
        renamedFiles.push(
          new File([fileToRename], cleanUpFileName(fileToRename.name), {
            type: fileToRename.type,
          })
        )
      }
    }
    return renamedFiles
  }

  useEffect(() => {
    filesRef.current = files
  }, [files])

  useEffect(() => {
    const handlePaste = (event) => {
      if (!event.clipboardData?.files.length) return
      if (files.length > 10) return maximumFileLengthReached()

      let newFiles: File[] = Array.from(event.clipboardData?.files)

      newFiles = renameFiles(event.clipboardData?.files)

      setFiles([...filesRef.current, ...newFiles])
    }

    editorWrapperEl.current?.addEventListener('paste', handlePaste)

    return () => {
      editorWrapperEl.current?.removeEventListener('paste', handlePaste)
    }
  }, [])

  const removeFile = (fileToRemove) => {
    const newFiles = files.filter((file) => file !== fileToRemove)
    setFiles(newFiles)
  }

  const appendFiles = (filesToAdd: any[] | FileList) => {
    if (files.length > 10) return maximumFileLengthReached()

    // clean up file names with spaces
    const renamedFiles = Array.from(filesToAdd ?? []).map((file) => {
      const fileName = cleanUpFileName(file.name)
      return new File([file], fileName, { type: file.type })
    })

    setFiles((files) => [...files, ...renamedFiles])
  }

  const handleAddFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files: filesToAdd = [] } = event.target
    appendFiles(filesToAdd)
  }

  return (
    <div className='border border-gray-200 rounded p-3 prose prose-sm min-w-full'>
      <div ref={editorWrapperEl}>
        <Toolbar>
          {(externalProps) => (
            <>
              <BoldButton {...externalProps} />
              <ItalicButton {...externalProps} />
              <UnderlineButton {...externalProps} />
              <linkPlugin.LinkButton {...externalProps} />
              <Separator />
              <UnorderedListButton {...externalProps} />
              <OrderedListButton {...externalProps} />
              <BlockquoteButton {...externalProps} />
            </>
          )}
        </Toolbar>
        <Editor
          editorState={editorState}
          onChange={onChange}
          placeholder={placeholder}
          plugins={plugins}
          ref={editor}
          data-test='note-editor'
          spellCheck
        />
        <MentionSuggestions
          open={open}
          suggestions={suggestions}
          onOpenChange={onOpenChange}
          onSearchChange={onSearchChange}
        />
        <EmojiSuggestions />
      </div>
      <FileListPreview
        files={files}
        clearFiles={removeFile}
      />

      <div
        className='flex gap-3 justify-end items-center mt-3'
        onDragEnter={_debounce((e) => {
          e.preventDefault()
          e.stopPropagation()
          e.target.style.backgroundColor = '#f3f4f6'
          e.target.style.border = '3px dashed #d1d5db'
        }, 100)}
        onDragLeave={_debounce((e) => {
          e.preventDefault()
          e.stopPropagation()
          e.target.style.backgroundColor = ''
          e.target.style.border = ''
        }, 100)}
        onDrop={(e) => {
          e.preventDefault()
          e.stopPropagation()
          const target = e.target as HTMLDivElement
          appendFiles(e.dataTransfer.files)
          target.style.backgroundColor = ''
          target.style.border = ''
        }}
      >
        <div className='flex-grow'>
          <label htmlFor='dropzoneFile'>
            <DocumentPlusIcon
              height={24}
              width={24}
              className='inline-block text-gray-400 cursor-pointer'
              data-test='streaming-note-editor-file-upload'
            />
            <input
              type='file'
              ref={inputFileEl}
              id='dropzoneFile'
              className='hidden'
              onChange={handleAddFile}
              multiple
              accept='application/pdf,image/*'
            />
          </label>
        </div>
        <button
          disabled={
            editorState.getCurrentContent().getPlainText().length === 0 ||
            isCreatingStreamingNote ||
            isLoading
          }
          className='text-gray-500 disabled:text-gray-300 enabled:bg-green-700 enabled:text-white px-3 py-1 enabled:rounded -mr-1'
          onClick={onSubmit}
        >
          {isCreatingStreamingNote || isLoading ? (
            <Spinner size={5} />
          ) : (
            <PaperAirplaneIcon
              height={22}
              width={22}
              data-test='streaming-note-editor-submit'
            />
          )}
        </button>
      </div>
    </div>
  )
}
